import {
  Scene,
  Color,
  Clock,
  Mesh,
  MeshPhongMaterial,
  HemisphereLight,
  DirectionalLight,
  DoubleSide,
  PlaneGeometry,
  TextureLoader,
  OrthographicCamera,
  WebGLRenderer,
  Vector2
} from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { AfterimagePass } from "three/examples/jsm/postprocessing/AfterimagePass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { CopyShader } from "three/examples/jsm/shaders/CopyShader.js";
// import Stats from "stats.js";

const FFT_SIZE = 2048;

class VisualAids {
  constructor() {
    this.init = this.init.bind(this);
    this.animate = this.animate.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.objects = [];
    this.cache = {};
    this.maxadata = [];
    this.lastMouseX = 0;
    this.timeOffset = 0;

    this.init();
  }

  loadOBJ(path) {
    if (this.cache[path]) return this.cache[path].clone(true);
    const loader = new OBJLoader();
    return new Promise((res, rej) => {
      loader.load(
        path,
        (o) => {
          this.cache[path] = o;
          res(o.clone(true));
        },
        null,
        (e) => rej(e)
      );
    });
  }

  async init() {
    this.aspect = window.innerWidth / window.innerHeight;
    this.camera = new OrthographicCamera(
      window.innerWidth / -2,
      window.innerWidth / 2,
      window.innerHeight / 2,
      window.innerHeight / -2,
      1.25,
      1250
    );
    this.camera.zoom = window.innerHeight / 512;
    this.camera.updateProjectionMatrix();
    this.camera.position.z = 300;
    this.camera.position.x = -300;
    this.camera.position.y = -300;
    this.camera.rotation.x = Math.PI / 4;
    this.camera.rotation.y = Math.PI * 1.75;
    this.camera.rotation.z = -Math.PI / 4;

    // this.controls = new OrbitControls(this.camera, document.documentElement);

    this.scene = new Scene();
    this.scene.background = new Color("#201A47");

    const len = 32;
    const startX = -512;
    const spl = 512 / len;
    const radius = 128;
    let points = [];
    const checkForPossibleIntersection = (obj, i = 4) => {
      if (!i) return this.scene.remove(obj);
      for (let point of points) {
        if (
          obj.position.y >= point - radius &&
          obj.position.y < point + radius
        ) {
          const pNFactor = Math.sign(point - obj.position.y);
          const distance = Math.abs(obj.position.y - point);
          obj.position.y += (radius - distance) * pNFactor;
          if (obj.position < -256) obj.position += 512;
          if (obj.position > 256) obj.position -= 512;
          checkForPossibleIntersection(obj, i - 1);
          break;
        }
      }
    };
    for (let i = 0; i < len; i++) {
      const obj = await this.loadOBJ("/monitorhead/monitorhead.obj");
      const texture = new TextureLoader().load("/monitorhead/monitorhead.png");
      this.scene.add(obj);
      obj.children[0].material.map = texture;
      obj.position.x = startX + spl * i;
      obj.position.y -= 64 + (Math.random() - 0.5) * 8 * 512;
      checkForPossibleIntersection(obj);
      obj.position.z -= 64;
      obj.scale.x *= 32;
      obj.scale.y *= 32;
      obj.scale.z *= 32;
      obj.rotation.x += Math.PI / 4;
      obj.rotation.y += Math.PI / 8 + Math.random() * Math.PI * 2;

      obj.backupY = obj.position.y;
      obj.backupZ = obj.position.z;
      obj.speed = Math.random() * 1.25;
      this.objects.push(obj);
      points.push(obj.position.y);
    }

    this.geometry = new PlaneGeometry(2048, 2048, 16, 16);
    this.material1 = new MeshPhongMaterial({
      color: 0xa83a94,
      side: DoubleSide,
      wireframe: true,
      wireframeLinewidth: 8
    });
    this.material2 = new MeshPhongMaterial({
      color: 0x201a47,
      side: DoubleSide
    });
    this.material2.transparent = true;
    this.material2.opacity = 0.5;
    this.material2.flatShading = true;
    this.mesh1 = new Mesh(this.geometry, this.material1);
    this.mesh2 = new Mesh(this.geometry, this.material2);
    this.scene.add(this.mesh1);
    this.scene.add(this.mesh2);

    const skyColor = 0xfce7a2;
    const groundColor = 0x221330;
    const intensity = 1;
    this.light = new HemisphereLight(skyColor, groundColor, intensity);
    this.scene.add(this.light);

    const color = 0xf68484;
    this.directionallight = new DirectionalLight(color, intensity * 4);
    this.directionallight.position.set(5, 0, 2);
    this.directionallight.target.position.set(-5, 0, -2);
    const color2 = 0xfffab2;
    this.directionallight2 = new DirectionalLight(color2, intensity);
    this.directionallight2.position.set(5, 0, 2);
    this.directionallight2.target.position.set(-5, 0, -2);
    this.scene.add(this.directionallight);
    this.scene.add(this.directionallight.target);
    this.scene.add(this.directionallight2);
    this.scene.add(this.directionallight2.target);

    this.clock = new Clock();

    this.renderer = new WebGLRenderer({
      powerPreference: "high-performance",
      antialias: true
    });

    this.effectComposer = new EffectComposer(this.renderer);
    this.render = new RenderPass(this.scene, this.camera);
    this.afterimage = new AfterimagePass(0.9);
    this.bloom = new UnrealBloomPass(new Vector2( window.innerWidth, window.innerHeight ), 2, 0.1, 0.75);
    this.copy = new ShaderPass(CopyShader);
    this.copy.renderToScreen = true;
    this.effectComposer.addPass(this.render);
    this.effectComposer.addPass(this.afterimage);
    this.effectComposer.addPass(this.bloom);
    this.effectComposer.addPass(this.copy);

    document.body.appendChild(this.renderer.domElement);
    window.addEventListener("resize", this.onWindowResize);
    window.addEventListener("mousemove", this.onMouseMove);

    // this.stats = new Stats();
    // document.body.appendChild(this.stats.dom);

    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.effectComposer.setPixelRatio(window.devicePixelRatio);
    this.effectComposer.setSize(window.innerWidth, window.innerHeight);
    this.effectComposer.render();
    this.renderer.setAnimationLoop(this.animate);
  }

  lerp(a, b, t) {
    return a * (1 - t) + b * t;
  }

  animate() {
    const dt = this.clock.getDelta() * 1000;
    this.deltaMouse = this.lerp(this.deltaMouse || 0, 0, 0.025 * (60 / 1000) * dt);
    this.timeOffset += this.deltaMouse * (0.005 * (60 / 1000)) * dt;

    const t = this.clock.getElapsedTime();
    const buffer = this.geometry.getAttribute("position");
    let coll = [];
    for (let i = 0; i < this.maxadata.length; i++)
      this.maxadata[i] = this.lerp(this.maxadata[i], 0, 0.01 * (60 / 1000) * dt);
    const a = buffer.array.map((v, k) => {
      const i = k % buffer.itemSize;
      coll[i] = v;
      if (i === buffer.itemSize - 1) {
        let calculated = Math.sin(coll[0] * 4 + coll[1] / 3 - t - this.timeOffset) * 16;
        if (this.maxadata.length > 0)
          calculated = this.lerp(calculated, calculated + this.maxadata[k % this.maxadata.length] * 2 - 2 ** 8, 0.5);
        coll = [];
        return calculated;
      }
      return v;
    });
    buffer.set(a);
    buffer.needsUpdate = true;

    for (const obj of this.objects) {
      obj.position.z = obj.backupZ + Math.sin(t) * 16;
      obj.rotation.y += 0.001 * Math.max(this.deltaMouse / 4, 1);
      obj.position.x += obj.speed * Math.max(this.deltaMouse / 4, 1);

      if (obj.position.x >= 512 + 64) obj.position.x = -512;
    }

    // this.controls.update();
    this.effectComposer.render();

    // this.stats.end();
  }

  onWindowResize() {
    this.camera.left = window.innerWidth / -2;
    this.camera.right = window.innerWidth / 2;
    this.camera.top = window.innerHeight / 2;
    this.camera.bottom = window.innerHeight / -2;
    this.camera.zoom = window.innerHeight / 512;
    this.camera.updateProjectionMatrix();

    this.bloom.resolution.x = window.innerWidth;
    this.bloom.resolution.y = window.innerHeight;

    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.effectComposer.setSize(window.innerWidth, window.innerHeight);
  }

  updateAudio(adata) {
    for (let i = 0; i < adata.length; i++)
      this.maxadata[i] = Math.max(this.maxadata[i] || 0, adata[i]);
  }

  onMouseMove(e) {
    if (this.noMouseMove) return;
    const deltaMouse = Math.abs(e.clientX - this.lastMouseX) / 2;
    if (deltaMouse === 0) return;
    this.deltaMouse += Math.min(deltaMouse, 4);
    this.lastMouseX = e.clientX;
  }
}

(function() {
  const state = Math.floor(Math.random() * 3);

  let choice;
  switch(state) {
    case 0:
      choice = 'radio';
      break;
    case 1:
      choice = 'video';
      break;
    case 2:
      choice = 'img';
      break;
  }
  const image = document.createElement(choice);
  image.className = 'it';
  const span = document.createElement('span');
  span.className = 'hint';
  let mx = 0;
  let my = 0;
  window.addEventListener('mousemove', function(e) {
    mx = e.clientX;
    my = e.clientY;
  });

  const randomImages = [
    '/nfttechnology.png',
    '/jimdavis.png',
    '/just.png',
    '/hammer.gif',
    '/monday-wtf.gif',
    '/mspaint.png',
    '/ahaha.png',
    '/fring.png',
    '/fuckingspeaker.png',
    '/korn.jpg',
    '/mrbreast.jpg',
    '/murder.jpg',
    '/rawmeat.png',
    '/xxx.png',
    '/unknown.png',
    '/telegramfurry.png',
  ];
  const randomVideos = [
    '/idiot.mp4',
    '/at.mp4',
    '/chinesemarket.mp4',
    '/fire.mp4',
    '/info.mp4',
    '/strange.mp4',
    '/unex.mp4',
    '/crunchy.mp4',
    '/americanpsycho.mov',
    '/cheesecake.mp4',
    '/inside.mp4',
    '/russian.mp4',
    '/russian2.mp4',
    '/shaman.mp4',
    '/shitbox.mp4',
    '/yippee.mp4',
    '/cookies.mp4',
    '/door.mp4',
    '/fire2.mp4',
    '/garfield.mp4',
    '/shockwaves.mp4',
    '/simpsons.mp4',
    '/steveo.mp4',
    '/vibrate.mp4'
  ];
  let chosenArray;

  chosenArray = choice === 'video' ? randomVideos : randomImages;
  image.src = '/media' + chosenArray[Math.floor(Math.random() * chosenArray.length)];

  const ctx = new (window.AudioContext || window.webkitAudioContext)();

  let audio;
  let analy;
  let buflen;
  let adata;

  if (choice === 'video') {
    image.loop = true;
    function play() { image.play(); window.removeEventListener('click', play); span.remove(); }
    window.addEventListener('click', play);

    span.innerText = 'CLICK FOR FUNNY VIDEO';
  } else if (choice === 'radio') {
    function play() {
      ctx.resume();
      audio = document.createElement('audio');
      audio.crossOrigin = 'anonymous';
      audio.src = 'http://radio.nahuy.life/beelive.ogg';

      analy = ctx.createAnalyser();
      analy.fftSize = FFT_SIZE;

      buflen = analy.frequencyBinCount;
      adata = new Uint8Array(buflen);
      analy.getByteTimeDomainData(adata);

      const source = ctx.createMediaElementSource(audio);
      source.connect(analy);
      source.connect(ctx.destination);

      audio.play();

      window.removeEventListener('click', play);
      span.remove();
    }
    window.addEventListener('click', play);
    image.style.display = 'none';
    document.body.style.cursor = 'auto';

    span.innerText = 'CLICK FOR RADIO';
  }

  document.body.prepend(span, image);

  const spring = 0.05;
  const friction = 0.9;
  let vx = 0, vy = 0;

  const spring1 = 0.1;
  const friction1 = 0.9;
  let vx1 = 0, vy1 = 0;

  function updateVI(dt) {
    const rect = { x: Number(image.style.left.slice(0, -2)), y: Number(image.style.top.slice(0, -2)) };
    let dx = mx - rect.x,
      dy = my - rect.y,
      ax = dx * spring * (60 / 1000),
      ay = dy * spring * (60 / 1000);

    vx += ax * dt;
    vy += ay * dt;
    vx *= friction;
    vy *= friction;

    image.style.top = rect.y + vy + 'px';
    image.style.left = rect.x + vx + 'px';
  }

  function updateSpan(dt) {
    const rect = { x: Number(span.style.left.slice(0, -2)), y: Number(span.style.top.slice(0, -2)) };
    let dx = mx - rect.x,
      dy = my - rect.y,
      ax = dx * spring1 * (60 / 1000),
      ay = dy * spring1 * (60 / 1000);

    vx1 += ax * dt;
    vy1 += ay * dt;
    vx1 *= friction1;
    vy1 *= friction1;

    const xy = Date.now() / 1000 * Math.PI;
    const radius = 128;
    const cos = Math.cos(xy) * radius;
    const sin = Math.sin(xy) * radius;

    span.style.top = rect.y + vy1 + sin + 'px';
    span.style.left = rect.x + vx1 + cos + 'px';
  }

  let a;

  let last = 0;
  const update = function(t) {
    let dt = t - last;
    last = t;
    updateVI(dt);
    if (span) updateSpan(dt);
    if (choice === 'radio' && audio) {
      buflen = analy.frequencyBinCount;
		  adata = new Uint8Array(buflen);
		  analy.getByteTimeDomainData(adata);

      if (a)
        a.updateAudio(adata);
    }

    window.requestAnimationFrame(update);
  }

  window.requestAnimationFrame(update);

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  }

  // remove cookie function
  function eraseCookie(name) {
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT';
  }

  const reset = document.querySelector('.reset');
  reset.addEventListener('click', function() {
    eraseCookie('disable-visual-aids');
    eraseCookie('never-show');
    window.location.reload();
  });

  if (getCookie('disable-visual-aids') !== 'true') {
    a = new VisualAids();
    if (choice === 'radio')
      a.noMouseMove = true;
  } else return;
  if (getCookie('never-show') === 'true') return;
  reset.remove();

  const warning = document.querySelector('.warning');
  const no = document.querySelector('#no');
  const noForever = document.querySelector('#no-forever');
  const neverShow = document.querySelector('#never-show');
  const close = document.querySelector('#close');

  warning.classList.add('show');
  no.addEventListener('click', function() {
    a.renderer.setAnimationLoop(null);
    warning.classList.remove('show');
  });

  noForever.addEventListener('click', function() {
    document.cookie = 'disable-visual-aids=true; max-age=31536000';
    a.renderer.setAnimationLoop(null);
    warning.classList.remove('show');
  });

  neverShow.addEventListener('click', function() {
    document.cookie = 'never-show=true; max-age=31536000';
    warning.classList.remove('show');
  });

  close.addEventListener('click', function() {
    warning.classList.remove('show');
  });
})()